import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { AdvancedImage } from "@cloudinary/react";
import { fill } from "@cloudinary/url-gen/actions/resize";
import localeText from "../../../../locale";
import { formatPrice } from "../../../../utilities/formatPrice";
import { cloudinaryInstance } from "../../../../utilities/Cloudinary/cloudinaryInstance";
import AdminService from "../../../../service/AdminService";
import useAdminList, { expectCode } from "../../hooks/useAdminList";
import { buildSearchText, formatDay, getToken, normalizeText } from "../../utils/adminUtils";
import StatusTabs from "../../components/list/StatusTabs";
import SearchInput from "../../components/list/SearchInput";
import StatusBadge from "../../components/list/StatusBadge";
import EmptyState from "../../components/list/EmptyState";
import "./AdminOrders.css";

const ALL = "all";

// PAID = pago e aguardando envio pelo vendedor, por isso vem destacado
const ORDER_STATUS = {
  PAID: { labelKey: "paid", tone: "warning", icon: "pi pi-clock" },
  SENT: { labelKey: "sent", tone: "neutral", icon: "pi pi-truck" },
  RECEIVED: { labelKey: "received", tone: "good", icon: "pi pi-check-circle" },
  PENDING: { labelKey: "pending", tone: "warning", icon: "pi pi-hourglass" },
};

const MAX_THUMBS = 4;

const utcDayKey = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
};

const localDayKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

const personName = (person) =>
  `${person?.firstname || ""} ${person?.surname || ""}`.trim() || person?.username || "—";

const loadOrders = async () => {
  const res = await new AdminService().getAllOrders(getToken());
  return expectCode(res, 200001).result;
};

const AdminOrders = () => {
  const history = useHistory();
  const { items, loading, loadError, reload } = useAdminList(loadOrders);
  const [tab, setTab] = useState(ALL);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState(null);

  const rows = useMemo(
    () =>
      items.map((order) => ({
        ...order,
        dateValue: order.date ? new Date(order.date).getTime() : 0,
        dayKey: order.date ? utcDayKey(order.date) : null,
        itemsCount: order.itens?.length || 0,
        currency: order.itens?.[0]?.product?.currency,
        amountValue: order.amountTotal ?? order.amountOrder ?? null,
        searchText: buildSearchText([
          String(order.id ?? ""),
          order.buyer?.username,
          order.buyer?.firstname,
          order.buyer?.surname,
          order.seller?.username,
          order.seller?.firstname,
          order.seller?.surname,
          order.seller?.companyName,
        ]),
      })),
    [items],
  );

  const counts = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({ ...acc, [row.status]: (acc[row.status] || 0) + 1 }),
        { [ALL]: rows.length },
      ),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const term = normalizeText(search.trim());
    const [start, end] = dateRange || [];
    const startKey = start ? localDayKey(start) : null;
    const endKey = end ? localDayKey(end) : startKey;

    return rows.filter((row) => {
      if (tab !== ALL && row.status !== tab) return false;
      if (term && !row.searchText.includes(term)) return false;
      if (startKey && (!row.dayKey || row.dayKey < startKey || row.dayKey > endKey)) return false;
      return true;
    });
  }, [rows, tab, search, dateRange]);

  const hasFilters = !!(search.trim() || dateRange?.[0]);

  const clearFilters = () => {
    setSearch("");
    setDateRange(null);
  };

  const openOrder = (order) => history.push(`/dashboard-purchasesdetails?orderId=${order.id}`);

  const orderTemplate = (order) => {
    const itens = order.itens || [];
    return (
      <div className="admin-orders__order">
        <div className="admin-orders__thumbs" aria-hidden="true">
          {itens.slice(0, MAX_THUMBS).map((item) => {
            const imageId = item.productVariation
              ? item.productVariation.imageid
              : item.product?.imageid1;
            return imageId ? (
              <AdvancedImage
                key={`thumb-${item.id}`}
                cldImg={cloudinaryInstance
                  .image(imageId)
                  .resize(fill().width(72).height(72))
                  .format("webp")}
                className="admin-orders__thumb"
                width={36}
                height={36}
              />
            ) : (
              <span key={`thumb-${item.id}`} className="admin-orders__thumb admin-orders__thumb--empty">
                <i className="pi pi-image" />
              </span>
            );
          })}
          {itens.length > MAX_THUMBS && (
            <span className="admin-orders__thumb admin-orders__thumb--more">
              +{itens.length - MAX_THUMBS}
            </span>
          )}
        </div>
        <div className="admin-stack">
          <span className="admin-strong">#{order.id}</span>
          <span className="admin-muted">
            {order.itemsCount} {localeText("adminOrdersItems")}
          </span>
        </div>
      </div>
    );
  };

  const personTemplate = (person, showCompany = false) => (
    <div className="admin-stack">
      <span>{showCompany && person?.companyName ? person.companyName : personName(person)}</span>
      {person?.username && <span className="admin-muted">@{person.username}</span>}
    </div>
  );

  const statusTemplate = (order) => {
    const config = ORDER_STATUS[order.status];
    if (!config) return <StatusBadge tone="neutral">{order.status || "—"}</StatusBadge>;
    return (
      <StatusBadge tone={config.tone} icon={config.icon}>
        {localeText(config.labelKey)}
      </StatusBadge>
    );
  };

  return (
    <div className="admin-list">
      <StatusTabs
        tabs={[
          { value: ALL, label: localeText("adminUsersStatusAll") },
          { value: "PAID", label: localeText("adminOrdersAwaitingShipment") },
          { value: "SENT", label: localeText("sent") },
          { value: "RECEIVED", label: localeText("received") },
          { value: "PENDING", label: localeText("pending") },
        ]}
        value={tab}
        onChange={setTab}
        counts={counts}
        loading={loading}
      />

      <section className="admin-list__card">
        <div className="admin-list__toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={localeText("adminOrdersSearchPlaceholder")}
          />
          <Calendar
            value={dateRange}
            onChange={(e) => setDateRange(e.value)}
            selectionMode="range"
            readOnlyInput
            showIcon
            showButtonBar
            maxDate={new Date()}
            dateFormat="dd/mm/yy"
            placeholder={localeText("date")}
            className="admin-list__calendar"
          />
          {hasFilters && (
            <button type="button" className="admin-link-btn" onClick={clearFilters}>
              <i className="pi pi-filter-slash" aria-hidden="true" />
              {localeText("adminUsersClearFilters")}
            </button>
          )}
          <span className="admin-list__result-count">
            {loading ? "" : `${filteredRows.length} ${localeText("adminUsersResults")}`}
          </span>
        </div>

        <div className="admin-list__table">
          <DataTable
            value={filteredRows}
            dataKey="id"
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 20, 50]}
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate={localeText("showingFirstToLast")}
            sortField="dateValue"
            sortOrder={-1}
            removableSort
            scrollable
            tableStyle={{ minWidth: "70rem" }}
            rowClassName={(args) => {
              // PrimeReact 10 pode passar [rowData, options] para o rowClassName
              const order = Array.isArray(args) ? args[0] : args;
              return {
                "admin-orders__row": true,
                "admin-orders__row--attention": order?.status === "PAID",
              };
            }}
            onRowClick={(e) => openOrder(e.data)}
            emptyMessage={
              loadError ? (
                <EmptyState
                  icon="pi pi-exclamation-triangle"
                  message={localeText("adminListLoadError")}
                  actionLabel={localeText("adminUsersRetry")}
                  onAction={reload}
                />
              ) : (
                <EmptyState
                  icon="pi pi-inbox"
                  message={localeText(hasFilters ? "adminListNoResults" : "adminListEmpty")}
                  actionLabel={hasFilters ? localeText("adminUsersClearFilters") : null}
                  onAction={clearFilters}
                />
              )
            }
          >
            <Column header={localeText("adminOrdersOrder")} field="id" body={orderTemplate} sortable />
            <Column
              header={localeText("byBuyer")}
              field="buyer.username"
              body={(order) => personTemplate(order.buyer)}
              sortable
            />
            <Column
              header={localeText("seller")}
              field="seller.username"
              body={(order) => personTemplate(order.seller, true)}
              sortable
            />
            <Column
              header={localeText("totalAmount")}
              field="amountValue"
              body={(order) =>
                order.amountValue != null ? (
                  <span className="admin-num">
                    {`${order.currency || ""} ${formatPrice(Number(order.amountValue))}`.trim()}
                  </span>
                ) : (
                  <span className="admin-muted">—</span>
                )
              }
              sortable
            />
            <Column header={localeText("status")} field="status" body={statusTemplate} sortable />
            <Column
              header={localeText("date")}
              field="dateValue"
              body={(order) => formatDay(order.date)}
              sortable
            />
            <Column
              header={localeText("actions")}
              frozen
              alignFrozen="right"
              headerClassName="admin-list__actions-cell"
              bodyClassName="admin-list__actions-cell"
              body={(order) => (
                <button
                  type="button"
                  className="admin-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    openOrder(order);
                  }}
                >
                  <i className="pi pi-eye" aria-hidden="true" />
                  {localeText("orderDetails")}
                </button>
              )}
            />
          </DataTable>
        </div>
      </section>
    </div>
  );
};

export default AdminOrders;
